'use client';

import GroupRevealReminder from '@/components/GroupRevealReminder';

import { useSearchParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import Button, { buttonClasses } from '@/components/ui/Button';
import { mintHandoff } from '@/lib/handoff/client';
import { ArrowRight, Users } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { capturePosthogEvent } from '@/lib/posthog';
import {
  normalizeScore,
  toVisionScale,
  findVisionAlignment
} from './types';
import { AdaptivePoliticalCompass } from '../../../lib/adaptive-political-compass';
import React, { useRef, useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { loadGridData, findIdeologyByPosition, findDetailedIdeology, findDetailedIdeologyWithSupplementary, findDetailedIdeologyWithPredefinedMacroCell, findIdeologyByLabel, macroCellFromSupplementKeys, GridCellData } from '@/lib/grid-data-loader';
import { macroCellFromScores } from '@/lib/quiz/micro-ideology-matcher';
import { getCurrentSession, getSessionById, getLatestCompletedSession } from '@/lib/quiz-session';
import { debugLog, debugWarn, debugError } from '@/lib/debug-logger';

// Macro cell colors from the political compass
const MACRO_CELL_COLORS = {
  'EL-GA': '#ff9ea0',   // Revolutionary Communism & State Socialism
  'EM-GA': '#ff9fff',   // Authoritarian Statist Centrism
  'ER-GA': '#9f9fff',   // Authoritarian Right & Corporatist Monarchism
  'EL-GM': '#ffcfa1',   // Democratic Socialism & Left Populism
  'EM-GM': '#e5e5e5',   // Mixed-Economy Liberal Center
  'ER-GM': '#9ffffe',   // Conservative Capitalism & National Conservatism
  'EL-GL': '#9fff9e',   // Libertarian Socialism & Anarcho-Communism
  'EM-GL': '#d4fe9a',   // Social-Market Libertarianism
  'ER-GL': '#ffff9f'    // Anarcho-Capitalism & Ultra-Free-Market Libertarianism
};

// Helper function to get macro cell color
function getMacroCellColor(macroCellCode: string): string {
  return MACRO_CELL_COLORS[macroCellCode as keyof typeof MACRO_CELL_COLORS] || '#e5e5e5';
}

// Helper function to find grid data by ideology name (for alignments)
function findGridDataByIdeology(gridData: GridCellData[], ideologyName: string): GridCellData | null {
  return gridData.find(cell => 
    cell.ideology === ideologyName || 
    cell.friendlyLabel === ideologyName ||
    cell.macroCellLabel.includes(ideologyName)
  ) || null;
}
const ResultCube = lazy(() => import('../../../components/ResultCube'));
const ResultCubeFallback = lazy(() => import('../../../components/ResultCubeFallback'));
import CompactShareRow from '@/components/CompactShareRow';
import CompareCta from '@/components/CompareCta';
import ResultSatisfaction from '@/components/ResultSatisfaction';
import LongQuizReportCard from '@/components/LongQuizReportCard';
import AboutCreator from '../../../components/AboutCreator';
import SupplementAxes from '../../../components/SupplementAxes';
import PdfReportUpsell from '../../../components/PdfReportUpsell';
import { resolveReportEvidence } from '@/lib/report/answer-evidence';
import { loadAllQuestions, type Question } from '@/lib/question-loader';
import { loadSupplementAxes, SupplementAxis } from '@/lib/supplement-axes-loader';
import { saveQuizResult, getQuizResultById, getResultPercentage, getTotalQuizCount, getPoliticalGroupMatches, getSurprisingAlignments, testFirebaseConnection, getWaitlistCount } from '@/lib/quiz';
import { hasWebGL } from '@/lib/webgl';
import { loadPhase2QuestionData } from '@/lib/phase2-question-loader';

// Shape of question data passed to scoring; covers both Phase 1 and Phase 2.
// Phase 1 entries supply `axis` (economic/governance/social) while Phase 2
// entries supply `supplementAxis` (e.g. ELGA-A). The `axis` field is always
// present in payloads we control (Phase 2 uses 'supplementary' as a marker).
export interface ScoringQuestionData {
  id: number;
  // TSV id_code (e.g. "P05", "TBE-CR2"). Persisted so server-side flows
  // (compare creator-linkage) can map answers to the locked question set
  // without loading the TSV. Optional for backward compat with older rows.
  originalId?: string;
  axis: string;
  agreeDir: number;
  supplementAxis?: string;
  answer?: number | null;
}

export async function calculateScores(answers: number[], questionIds: number[], quizType: string = 'short', phase2Data?: Map<number, { supplementAxis: string; agreeDir: -1 | 1 }>, debugMode: boolean = false, questionData?: ScoringQuestionData[]) {
  let economicScore = 0;
  let governanceScore = 0;
  let socialScore = 0;
  let economicQuestions = 0;
  let governanceQuestions = 0;
  let socialQuestions = 0;

  // Phase 2 scoring for supplementary axes
  const supplementaryScores: Record<string, { score: number; count: number }> = {};

  // Convert continuous values (0-1) to score values (-2 to +2)
  const convertToScore = (value: number): number => {
    return (value - 0.5) * 4; // Maps 0->-2, 0.5->0, 1->2
  };

  // Legacy mode: if no questionIds provided, use hardcoded mapping
  if (questionIds.length === 0) {
    // Fallback to legacy hardcoded IDs for backward compatibility
    const legacyIds = quizType === 'long' 
      ? [1, 9, 17, 2, 10, 18, 3, 11, 19, 4, 12, 20, 5, 13, 21, 6, 14, 22, 7, 15, 23, 8, 16, 24, 25, 27, 29, 26, 28, 30]
      : [1, 2, 3, 4, 9, 10, 11, 12, 17, 18];
    questionIds = legacyIds.slice(0, answers.length);
  }

  // Load Phase 2 data if needed and not provided
  if (!phase2Data && questionIds.some(id => id >= 45)) {
    phase2Data = await loadPhase2QuestionData();
  }

  if (debugMode) {
    debugLog('\n🔍 ================== DEBUG SCORING AUDIT ==================');
    debugLog(`Quiz Type: ${quizType}`);
    debugLog(`Total Answers: ${answers.length}`);
    debugLog(`Question IDs: ${questionIds.length > 0 ? `${questionIds.length} questions` : 'Legacy mode'}`);

    // Count null vs non-null answers
    const nullAnswers = answers.filter(a => a === null || a === undefined).length;
    const validAnswers = answers.length - nullAnswers;
    debugLog(`📊 Answer Status: ${validAnswers} answered, ${nullAnswers} skipped`);

    // Show first few answers to verify structure
    debugLog('📋 Answer Array Sample (first 10):');
    answers.slice(0, 10).forEach((answer, i) => {
      const status = answer === null || answer === undefined ? 'SKIPPED' : 'ANSWERED';
      const qId = questionIds[i] || 'unknown';
      debugLog(`  [${i}] Q${qId}: ${status} (value: ${answer})`);
    });

    debugLog('\n📋 QUESTION-BY-QUESTION BREAKDOWN:');
    debugLog('Index | QID   | Axis        | Answer | Score | AgreeDir | Source    | Contribution');
    debugLog('------|-------|-------------|--------|-------|----------|-----------|-------------');
  }

  // Track running totals and validation issues
  const runningTotals = { economic: 0, governance: 0, social: 0 };
  const validationIssues: string[] = [];
  let questionIndex = 0;

  // Process each answer with its corresponding question ID
  // Note: Skipped questions are now filtered out completely before reaching here
  answers.forEach((continuousValue, index) => {
    if (index >= questionIds.length) return; // Skip if we don't have a question ID
    if (continuousValue === null || continuousValue === undefined || isNaN(continuousValue)) {
      if (debugMode) {
        const questionId = questionIds[index];
        debugLog(`⚠️  INVALID Q${questionId}: value=${continuousValue} (unexpected - should be filtered)`);
      }
      return; // Skip invalid values (shouldn't happen with new approach)
    }
    
    const questionId = questionIds[index];
    const score = convertToScore(continuousValue);
    
    // Check if this is a Phase 2 question (ID >= 45)
    if (questionId >= 45) {
      // Handle Phase 2 supplementary axis questions
      // Use ID-based lookup instead of index-based to handle filtered data correctly
      const questionInfo = questionData?.find(q => q.id === questionId);
      if (debugMode && questionId >= 45) {
        debugLog(`🔍 Looking for Q${questionId} in questionData (length: ${questionData?.length || 0})`);
        debugLog(`🔍 Found questionInfo:`, questionInfo ? `Q${questionInfo.id} suppAxis=${questionInfo.supplementAxis}` : 'NOT FOUND');
      }
      if (questionInfo && questionInfo.supplementAxis) {
        const { supplementAxis, agreeDir } = questionInfo;

        // Initialize axis if not exists
        if (!supplementaryScores[supplementAxis]) {
          supplementaryScores[supplementAxis] = { score: 0, count: 0 };
        }

        // Apply agree direction: -1 means agreeing is negative on axis, 1 means agreeing is positive
        const contribution = (agreeDir || 1) * score;
        supplementaryScores[supplementAxis].score += contribution;
        supplementaryScores[supplementAxis].count++;

        if (debugMode) {
          const paddedIndex = String(questionIndex).padStart(2, ' ');
          const paddedQID = String(questionId).padStart(5, ' ');
          const paddedAxis = supplementAxis.padEnd(11, ' ');
          const paddedAnswer = continuousValue.toFixed(3).padStart(6, ' ');
          const paddedScore = score.toFixed(2).padStart(5, ' ');
          const paddedDir = String(agreeDir).padStart(8, ' ');
          const paddedContrib = contribution.toFixed(2).padStart(11, ' ');
          debugLog(`${paddedIndex}    | ${paddedQID} | ${paddedAxis} | ${paddedAnswer} | ${paddedScore} | ${paddedDir} | ID-Based  | ${paddedContrib}`);
          debugLog(`🔄 ${supplementAxis} running total: ${supplementaryScores[supplementAxis].score.toFixed(2)} (count: ${supplementaryScores[supplementAxis].count})`);
        }

        // Validation
        if (!agreeDir || (agreeDir !== -1 && agreeDir !== 1)) {
          validationIssues.push(`Q${questionId}: Invalid agreeDir (${agreeDir})`);
        }
        if (continuousValue < 0 || continuousValue > 1) {
          validationIssues.push(`Q${questionId}: Answer out of bounds (${continuousValue})`);
        }

        questionIndex++;
      } else if (phase2Data) {
        // Fallback to phase2Data if available
        const phase2Question = phase2Data.get(questionId);
        if (phase2Question) {
          const { supplementAxis, agreeDir } = phase2Question;
          
          // Initialize axis if not exists
          if (!supplementaryScores[supplementAxis]) {
            supplementaryScores[supplementAxis] = { score: 0, count: 0 };
          }
          
          // Apply agree direction: -1 means agreeing is negative on axis, 1 means agreeing is positive
          const contribution = agreeDir * score;
          supplementaryScores[supplementAxis].score += contribution;
          supplementaryScores[supplementAxis].count++;
          
          if (debugMode) {
            const paddedIndex = String(questionIndex).padStart(2, ' ');
            const paddedQID = String(questionId).padStart(5, ' ');
            const paddedAxis = supplementAxis.padEnd(11, ' ');
            const paddedAnswer = continuousValue.toFixed(3).padStart(6, ' ');
            const paddedScore = score.toFixed(2).padStart(5, ' ');
            const paddedDir = String(agreeDir).padStart(8, ' ');
            const paddedContrib = contribution.toFixed(2).padStart(11, ' ');
            debugLog(`${paddedIndex}    | ${paddedQID} | ${paddedAxis} | ${paddedAnswer} | ${paddedScore} | ${paddedDir} | Fallback  | ${paddedContrib}`);
          }

          // Validation
          if (!agreeDir || (agreeDir !== -1 && agreeDir !== 1)) {
            validationIssues.push(`Q${questionId}: Invalid agreeDir (${agreeDir})`);
          }
          if (continuousValue < 0 || continuousValue > 1) {
            validationIssues.push(`Q${questionId}: Answer out of bounds (${continuousValue})`);
          }

          questionIndex++;
        } else {
          debugWarn(`Phase 2 question ${questionId} not found in data`);
          validationIssues.push(`Q${questionId}: Phase 2 question not found in fallback data`);
        }
      } else {
        debugWarn(`Phase 2 question ${questionId} has no supplementAxis data`);
        validationIssues.push(`Q${questionId}: No supplementAxis data available`);
      }
      questionIndex++;
      return;
    }
    
    // Use ID-based lookup instead of index-based to handle filtered data correctly
    const qData = questionData?.find(q => q.id === questionId);
    if (!qData) {
      debugWarn(`No question data found for question ID ${questionId}`);
      return;
    }
    const config = { axis: qData.axis };
    const agreeDir = qData.agreeDir || 1;

    let contribution = 0;
    let axisName = '';
    const dataSource = 'QuestionData';

    if (config.axis === 'economic') {
      contribution = score * agreeDir;
      economicScore += contribution;
      economicQuestions++;
      axisName = 'Economic';
      runningTotals.economic += contribution;
    } else if (config.axis === 'governance') {
      contribution = score * agreeDir;
      governanceScore += contribution;
      governanceQuestions++;
      axisName = 'Governance';
      runningTotals.governance += contribution;
    } else if (config.axis === 'social') {
      contribution = score * agreeDir;
      socialScore += contribution;
      socialQuestions++;
      axisName = 'Social';
      runningTotals.social += contribution;
    }
    
    if (debugMode) {
      const paddedIndex = String(questionIndex).padStart(2, ' ');
      const paddedQID = String(questionId).padStart(5, ' ');
      const paddedAxis = axisName.padEnd(11, ' ');
      const paddedAnswer = continuousValue.toFixed(3).padStart(6, ' ');
      const paddedScore = score.toFixed(2).padStart(5, ' ');
      const paddedDir = String(agreeDir).padStart(8, ' ');
      const paddedSource = dataSource.padEnd(9, ' ');
      const paddedContrib = contribution.toFixed(2).padStart(11, ' ');
      debugLog(`${paddedIndex}    | ${paddedQID} | ${paddedAxis} | ${paddedAnswer} | ${paddedScore} | ${paddedDir} | ${paddedSource} | ${paddedContrib}`);
    }

    // Validation for Phase 1 questions
    if (!agreeDir || (agreeDir !== -1 && agreeDir !== 1)) {
      validationIssues.push(`Q${questionId}: Invalid agreeDir (${agreeDir})`);
    }
    if (continuousValue < 0 || continuousValue > 1) {
      validationIssues.push(`Q${questionId}: Answer out of bounds (${continuousValue})`);
    }
    if (!config || !config.axis) {
      validationIssues.push(`Q${questionId}: Missing axis configuration`);
    }

    questionIndex++;
  });

  // Calculate max possible scores based on number of questions answered
  const maxEconomicScore = economicQuestions * 2;
  const maxGovernanceScore = governanceQuestions * 2;
  const maxSocialScore = socialQuestions * 2;

  // Prevent division by zero
  const economic = maxEconomicScore > 0 ? normalizeScore(economicScore, maxEconomicScore) : 0;
  const governance = maxGovernanceScore > 0 ? normalizeScore(governanceScore, maxGovernanceScore) : 0;
  const social = maxSocialScore > 0 ? normalizeScore(socialScore, maxSocialScore) : 0;

  // Convert supplementary scores to normalized values
  const supplementary: Record<string, number> = {};
  if (debugMode && Object.keys(supplementaryScores).length > 0) {
    debugLog('🔍 Supplementary scores before normalization:', supplementaryScores);
  }
  Object.entries(supplementaryScores).forEach(([axis, data]) => {
    if (data.count > 0) {
      const maxScore = data.count * 2;
      // Normalize from range [-maxScore, +maxScore] to [-100, +100] (same as main axes)
      supplementary[axis] = (data.score / maxScore) * 100;
    }
  });

  if (debugMode) {
    debugLog('\n📊 ==================== SCORING SUMMARY ====================');
    debugLog('\n🎯 PHASE 1 SCORES (Main Political Compass):');
    debugLog(`   Economic:  ${economic.toFixed(1)}/100  (Raw: ${economicScore.toFixed(2)}/${maxEconomicScore}, ${economicQuestions} questions)`);
    debugLog(`   Authority: ${governance.toFixed(1)}/100  (Raw: ${governanceScore.toFixed(2)}/${maxGovernanceScore}, ${governanceQuestions} questions)`);
    debugLog(`   Social:  ${social.toFixed(1)}/100  (Raw: ${socialScore.toFixed(2)}/${maxSocialScore}, ${socialQuestions} questions)`);
    
    if (Object.keys(supplementary).length > 0) {
      debugLog('\n🎯 PHASE 2 SCORES (Supplementary Axes):');
      Object.entries(supplementary).forEach(([axis, score]) => {
        const rawData = supplementaryScores[axis];
        debugLog(`   ${axis.padEnd(12, ' ')}: ${score.toFixed(1)}/100  (Raw: ${rawData.score.toFixed(2)}/${rawData.count * 2}, ${rawData.count} questions)`);
      });
    }

    debugLog('\n🏃 RUNNING TOTALS VERIFICATION:');
    debugLog(`   Economic:  ${runningTotals.economic.toFixed(2)} (should match raw score above)`);
    debugLog(`   Governance: ${runningTotals.governance.toFixed(2)} (should match raw score above)`);
    debugLog(`   Social:    ${runningTotals.social.toFixed(2)} (should match raw score above)`);

    if (validationIssues.length > 0) {
      debugLog('\n⚠️  VALIDATION ISSUES FOUND:');
      validationIssues.forEach(issue => debugLog(`   ❌ ${issue}`));
    } else {
      debugLog('\n✅ NO VALIDATION ISSUES FOUND');
    }
    
    debugLog('\n================================================================');
  }

  return { economic, governance, social, supplementary };
}


// Star background effect (copied and adapted from Home)
interface Star {
  top: number;
  left: number;
  size: string;
  color: string;
  rotate: string;
}
const NUM_STARS = 110;
const MIN_DIST = 7;
// Stars are placed in pixels against this fixed virtual height, not as a
// percentage of the page: the page grows while async sections load, and
// %-positioned stars all slid with every growth, which Chrome counted as
// layout shift (the mobile CLS failure in Search Console).
const STAR_FIELD_HEIGHT = 4200;
const SIZES = [
  'w-8 h-8', 'w-10 h-10', 'w-12 h-12', 'w-14 h-14', 'w-16 h-16', 'w-20 h-20',
  'w-24 h-24', 'w-28 h-28', 'w-32 h-32', 'w-40 h-40',
];
const COLORS = [
  'text-primary','text-secondary'
];
const ROTATIONS = ['rotate-0', 'rotate-3', 'rotate-6', 'rotate-12', '-rotate-3', '-rotate-6', '-rotate-12', 'rotate-45', '-rotate-45'];
function randomStar(existing: Star[]): Star {
  let tries = 0;
  while (tries < 100) {
    const top = Math.random() * 95 + 2;
    const left = Math.random() * 95 + 2;
    if (existing.every((s: Star) => {
      const dx = s.left - left;
      const dy = s.top - top;
      return Math.sqrt(dx * dx + dy * dy) > MIN_DIST;
    })) {
      return {
        top,
        left,
        size: SIZES[Math.floor(Math.random() * SIZES.length)] + ' md:' + SIZES[Math.floor(Math.random() * SIZES.length)],
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        rotate: ROTATIONS[Math.floor(Math.random() * ROTATIONS.length)],
      };
    }
    tries++;
  }
  return {
    top: Math.random() * 95 + 2,
    left: Math.random() * 95 + 2,
    size: SIZES[Math.floor(Math.random() * SIZES.length)] + ' md:' + SIZES[Math.floor(Math.random() * SIZES.length)],
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    rotate: ROTATIONS[Math.floor(Math.random() * ROTATIONS.length)],
  };
}
const starPattern: Star[] = [];
for (let i = 0; i < NUM_STARS; i++) {
  const isTop = i < Math.floor(NUM_STARS * 2 / 3);
  let star: Star;
  let tries = 0;
  do {
    star = randomStar(starPattern);
    if (isTop && star.top > 50) star.top = Math.random() * 45 + 2;
    tries++;
  } while (starPattern.some(s => Math.sqrt((s.left - star.left) ** 2 + (s.top - star.top) ** 2) < MIN_DIST) && tries < 10);
  starPattern.push(star);
}


function getOrdinalSuffix(n: number): string {
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) {
    return 'st';
  }
  if (j === 2 && k !== 12) {
    return 'nd';
  }
  if (j === 3 && k !== 13) {
    return 'rd';
  }
  return 'th';
}

export default function ResultsClient() {
  const posthog = usePostHog();
  const router = useRouter();
  const searchParams = useSearchParams();
  const firebaseId = searchParams.get('id'); // Firebase document ID
  const sessionIdParam = searchParams.get('sessionId');
  const dataParam = searchParams.get('data');
  const answersParam = searchParams.get('answers');
  const questionIdsParam = searchParams.get('questionIds');
  // The URL's type param is only a hint: links can carry the wrong type for
  // the row they point at (the group roster hardcoded type=long on short
  // rows, which fabricated a long result from 12 answers). Saved rows know
  // which quiz they were; once loaded, the row's own type overrides the URL.
  const urlQuizType = searchParams.get('type') || 'short';
  const [rowQuizType, setRowQuizType] = useState<'short' | 'long' | null>(null);
  const quizType = rowQuizType ?? urlQuizType;
  const isShared = searchParams.get('shared') === 'true';
  const isDebugMode = searchParams.get('debug') === 'true';
  // groupId is set when a long-quiz taker submits via group mode; the
  // contribution to the group already happened in the quiz page handler.
  // Here we just surface a banner linking back to the group.
  const groupIdParam = searchParams.get('groupId');
  // Set by /api/checkout/return's failure redirects; see the banner below.
  const purchasedParam = searchParams.get('purchased');
  const graphRef = useRef<HTMLDivElement>(null);
  const [, setGraphSize] = useState({ width: 0, height: 0 });
  const [docId, setDocId] = useState<string | null>(firebaseId);
  const hasSaved = useRef(false);
  const [view3D, setView3D] = useState(true);
  const [gridData, setGridData] = useState<GridCellData[]>([]);
  const [ideologyData, setIdeologyData] = useState<GridCellData | null>(null);
  const [supplementAxes, setSupplementAxes] = useState<SupplementAxis[]>([]);
  const [supplementScores, setSupplementScores] = useState<Record<string, number>>({});
  const [sessionMacroCellCode, setSessionMacroCellCode] = useState<string | null>(null);
  // Whether THIS browser holds a completed short-quiz session. Gates the
  // "your 12 answers carry over, you're 20% done" line in the long-quiz
  // CTAs: someone viewing a shared result never took the quiz here, so
  // promising carry-over to them would be false. Set in an effect to keep
  // SSR/hydration consistent (localStorage is client-only).
  const [hasCompletedShortHere, setHasCompletedShortHere] = useState(false);
  useEffect(() => {
    setHasCompletedShortHere(!!getLatestCompletedSession('short'));
  }, []);
  const hasLoadedAnalytics = useRef(false);
  // Guard so `quiz_results_viewed` fires once per page load. The ideology
  // resolver effect (loadIdeologyData) re-runs every time scores or
  // supplements settle; before the guard it fired ~6x per user.
  const hasFiredResultsViewed = useRef(false);
  // Initialize true when a firebaseId is present so the loading state is set
  // before the effect runs; this avoids a synchronous setState in the effect.
  const [isLoadingFirebase, setIsLoadingFirebase] = useState<boolean>(!!firebaseId);
  // Tracks the case where the persisted lookup returned a 404. The render
  // path uses this to show a graceful "result not found" message instead
  // of the loading spinner that would otherwise hang forever (the legacy
  // `dataLoaded` flag only flips when valid data lands, so a missing
  // record left the page stuck on "Loading quiz data...").
  const [resultMissing, setResultMissing] = useState<boolean>(false);

  // Store calculated scores
  const [scores, setScores] = useState<{
    economic: number;
    governance: number;
    social: number;
    supplementary: Record<string, number>;
  } | null>(null);

  // State for parsed data
  const [answers, setAnswers] = useState<number[]>([]);
  const [questionIds, setQuestionIds] = useState<number[]>([]);
  const [questionData, setQuestionData] = useState<ScoringQuestionData[]>([]);
  const [reportQuestionCatalog, setReportQuestionCatalog] = useState<Question[] | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  // Saved (?id=) results pin to the exact ideology stored when the quiz was
  // taken, instead of re-deriving it from scores (which drifts as the matcher
  // changes between deploys and made old saved links show the wrong result).
  const [loadedIdeologyLabel, setLoadedIdeologyLabel] = useState<string | null>(null);
  const [loadedMacroCellCode, setLoadedMacroCellCode] = useState<string | null>(null);
  
  // Session replay is disabled at SDK init on every route except a direct
  // /quiz/results entry (see PostHogProvider). The common path reaches results
  // via client-side navigation, where init already ran with recording off, so
  // we start it here, on the one page we record. startSessionRecording() obeys
  // the project-level sample rate, so only a sampled fraction of sessions persist.
  useEffect(() => {
    if (!posthog) return;
    try {
      posthog.startSessionRecording();
    } catch {
      // never let analytics break the results page
    }
  }, [posthog]);

  // Parse question data from Firebase, session or URL params
  useEffect(() => {
    let parsedAnswers: number[] = [];
    let parsedQuestionIds: number[] = [];
    let parsedQuestionData: ScoringQuestionData[] = [];
    
    // First priority: Load from Firebase if ID provided
    if (firebaseId) {
      debugLog('🔥 Loading result from Firebase:', firebaseId);
      // isLoadingFirebase initial state is already true when firebaseId is set
      // (see useState init above), so we don't need to call setState here.

      getQuizResultById(firebaseId)
        .then(data => {
          if (data) {
            debugLog('✅ Loaded from Firebase:', data);

            if (data.quizType === 'short' || data.quizType === 'long') {
              setRowQuizType(data.quizType);
            }

            // Set the pre-calculated scores directly
            if (data.result) {
              // Handle both old and new data formats
              const isNewFormat = data.result.governanceScore !== undefined;
              setScores({
                economic: data.result.economicScore,
                governance: isNewFormat ? data.result.governanceScore : data.result.socialScore, // New format or old authority score
                social: isNewFormat ? data.result.socialScore : (data.result.progressiveScore || 0), // New format or old cultural score
                supplementary: data.result.supplementaryScores || {}
              });
              
              // Also set supplementScores separately for ideology calculation
              if (data.result.supplementaryScores) {
                setSupplementScores(data.result.supplementaryScores);
              }
              
              // Pin the display to the ideology this taker actually got at save
              // time. We used to always re-derive it from scores on load, but the
              // matcher changes between deploys, so an old saved link could show a
              // different (often centrist) result than the one that was saved.
              // The ideology effect below prefers this stored label for ?id= loads
              // and only falls back to re-matching when it isn't found.
              const stored = data.result as { alignmentLabel?: string; macroCellCode?: string };
              setLoadedIdeologyLabel(stored.alignmentLabel ?? null);
              setLoadedMacroCellCode(stored.macroCellCode ?? null);
            }
            
            // Set answers and questionData for proper display
            if (data.answers) {
              setAnswers(data.answers);
              
              // Set questionData if available - this is crucial for proper
              // ideology calculation. D1 drops top-level questionData, so
              // newer rows nest a copy inside result_json; check both.
              const savedQuestionData = Array.isArray(data.questionData) && data.questionData.length > 0
                ? data.questionData
                : (data.result as { questionData?: ScoringQuestionData[] } | undefined)?.questionData;
              if (savedQuestionData) {
                setQuestionData(savedQuestionData);
                setQuestionIds(savedQuestionData.map((q: ScoringQuestionData) => q.id));
              } else if (data.quizType) {
                // Fallback: reconstruct questionIds based on quiz type if questionData not saved
                const longQuizPhase1Ids = [1, 9, 17, 2, 10, 18, 3, 11, 19, 4, 12, 20, 5, 13, 21, 6, 14, 22, 7, 15, 23, 8, 16, 24, 25, 27, 29, 26, 28, 30];
                const longQuizPhase2Ids = Array.from({ length: 24 }, (_, i) => 45 + i);
                const reconstructedIds = data.quizType === 'long' 
                  ? [...longQuizPhase1Ids, ...longQuizPhase2Ids]
                  : [1, 2, 3, 4, 9, 10, 11, 12, 17, 18];
                setQuestionIds(reconstructedIds.slice(0, data.answers.length));
              }
              
              setDataLoaded(true);
            }
            
            // Mark as already saved
            hasSaved.current = true;
          } else {
            debugError('No data found for Firebase ID:', firebaseId);
            setResultMissing(true);
          }
        })
        .catch(error => {
          debugError('Error loading from Firebase:', error);
          setResultMissing(true);
        })
        .finally(() => {
          setIsLoadingFirebase(false);
        });
      
      return; // Skip other loading methods
    }
    
    if (sessionIdParam) {
      debugLog('🔍 Attempting to load session:', sessionIdParam);
      debugLog('Window defined?', typeof window !== 'undefined');
      
      // Load from session
      const session = getSessionById(sessionIdParam);
      debugLog('Session loaded?', !!session);
      
      if (session) {
        debugLog('📂 Loading results from session:', sessionIdParam);
        debugLog('Session questions:', session.questions?.length || 0);
        debugLog('Session answers:', Object.keys(session.answers || {}).length);
        
        // Validate session has required data
        if (!session.questions || !session.answers) {
          debugError('Session missing questions or answers:', session);
          return;
        }
        
        const sessionAnswers = session.answers;
        parsedAnswers = session.questions.map(q => sessionAnswers[q.id] ?? 0.5);
        parsedQuestionIds = session.questions.map(q => q.id);
        parsedQuestionData = session.questions.map(q => ({
          id: q.id,
          axis: q.axis,
          agreeDir: q.agreeDir,
          answer: sessionAnswers[q.id] ?? 0.5,
          supplementAxis: q.supplementAxis
        }));
        
        debugLog('Parsed answers:', parsedAnswers.length);
        debugLog('Parsed question IDs:', parsedQuestionIds.length);

        // Store the macro cell from the session if it's a long quiz
        if (session.macroCellCode && session.type === 'long') {
          // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot parse of session data into state during initial mount; not a render-driven sync
          setSessionMacroCellCode(session.macroCellCode);
          debugLog('📍 Using macro cell from session:', session.macroCellCode);
        }
      } else {
        debugError('Session not found:', sessionIdParam);
        debugLog('Checking localStorage directly...');
        const stored = localStorage.getItem('votely_quiz_session_all');
        if (stored) {
          const all = JSON.parse(stored);
          debugLog('Available sessions:', Object.keys(all));
        }
      }
    } else if (dataParam) {
      // New format: decode base64 question data
      try {
        // The decoded payload is untrusted and shape-loose; treat the
        // top-level as `unknown` and narrow per-branch using ScoringQuestionData.
        const decoded: unknown = JSON.parse(atob(decodeURIComponent(dataParam)));
        const decodedObj = decoded as { questionData?: ScoringQuestionData[]; skipStats?: unknown };

        // Check if this is the new format with skipStats or the old format
        if (decodedObj.questionData && decodedObj.skipStats) {
          // New format with skip statistics
          parsedQuestionData = decodedObj.questionData;
          parsedAnswers = decodedObj.questionData.map((q: ScoringQuestionData) => q.answer ?? 0.5);
          parsedQuestionIds = decodedObj.questionData.map((q: ScoringQuestionData) => q.id);

          // DEBUG: Log the received data structure
          debugLog('🔍 RECEIVED DATA STRUCTURE (New Format):');
          debugLog('📊 Skip statistics:', decodedObj.skipStats);
          debugLog('📋 QuestionData length:', decodedObj.questionData.length);
          debugLog('📋 Sample questionData entries:');
          decodedObj.questionData.slice(0, 5).forEach((q: ScoringQuestionData, i: number) => {
            debugLog(`  [${i}] Q${q.id}: answer=${q.answer}, axis=${q.axis}, suppAxis=${q.supplementAxis}`);
          });
          debugLog('📊 Parsed answers:', parsedAnswers.slice(0, 10));
          debugLog('📊 Null/undefined answers:', parsedAnswers.filter(a => a === null || a === undefined).length);

          // Store skip statistics for later use (e.g., Firebase analytics)
          if (decodedObj.skipStats) {
            // We'll use these when saving to Firebase
            (window as Window & { __skipStats?: unknown }).__skipStats = decodedObj.skipStats;
          }
        } else if (Array.isArray(decoded)) {
          // Old format (backward compatibility)
          const decodedArray = decoded as ScoringQuestionData[];
          parsedQuestionData = decodedArray;
          parsedAnswers = decodedArray.map((q: ScoringQuestionData) => q.answer ?? 0.5);
          parsedQuestionIds = decodedArray.map((q: ScoringQuestionData) => q.id);
        } else {
          debugError('Unknown data format');
        }
      } catch (error) {
        debugError('Failed to decode question data:', error);
      }
    } else if (answersParam) {
      // Legacy format: separate answers and question IDs
      parsedAnswers = answersParam.split(',').map(val => {
        const num = parseFloat(val);
        return isNaN(num) ? 0.5 : num; // Default to neutral if parsing fails
      });
      parsedQuestionIds = questionIdsParam ? questionIdsParam.split(',').map(id => parseInt(id)) : [];
      
      // For legacy URLs with 50 answers but no question IDs, try to reconstruct
      if (parsedAnswers.length === 50 && parsedQuestionIds.length === 0 && urlQuizType === 'long') {
        debugLog('⚠️ Reconstructing quiz from legacy URL with 50 answers');
        // Phase 1: 30 questions with standard IDs
        const phase1Ids = [1, 9, 17, 2, 10, 18, 3, 11, 19, 4, 12, 20, 5, 13, 21, 6, 14, 22, 7, 15, 23, 8, 16, 24, 25, 27, 29, 26, 28, 30];
        // Phase 2: 24 questions with high IDs (we'll assign generic ones)
        const phase2Ids = Array.from({length: 24}, (_, i) => 45 + i);
        parsedQuestionIds = [...phase1Ids, ...phase2Ids];
        debugLog('🔄 Using reconstructed question IDs for scoring');
      }
    }
    
    setAnswers(parsedAnswers);
    setQuestionIds(parsedQuestionIds);
    setQuestionData(parsedQuestionData);
    setDataLoaded(true);

    // DEBUG: Comprehensive data loading log
    debugLog('📥 DATA PARSING COMPLETE:');
    debugLog('📊 Parsed answers length:', parsedAnswers.length);
    debugLog('📊 Parsed questionIds length:', parsedQuestionIds.length);
    debugLog('📊 Parsed questionData length:', parsedQuestionData.length);
    debugLog('📊 Setting dataLoaded to: true');
    debugLog('📊 Sample parsed answers:', parsedAnswers.slice(0, 10));
    debugLog('📊 Null answers in parsed data:', parsedAnswers.filter(a => a === null || a === undefined).length);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- firebaseId only used for initial param parsing; including it would re-parse on every load
  }, [sessionIdParam, dataParam, answersParam, questionIdsParam, urlQuizType]);

  useEffect(() => {
    if (!graphRef.current) return;
    const rect = graphRef.current.getBoundingClientRect();
    setGraphSize({ width: rect.width, height: rect.height });
  }, []);
  
  // Calculate scores when data is loaded
  useEffect(() => {
    // DEBUG: Log useEffect trigger conditions
    debugLog('🔄 RESULTS useEffect triggered:', {
      dataLoaded,
      answersLength: answers.length,
      questionIdsLength: questionIds.length,
      questionDataLength: questionData.length,
      quizType,
      isDebugMode
    });

    // Wait for data to be loaded and check if we have answers
    if (!dataLoaded || answers.length === 0) {
      debugLog('❌ Skipping computeScores - dataLoaded:', dataLoaded, 'answers.length:', answers.length);
      return;
    }

    // calculateScores maps each answer to its axis via questionData; with no
    // questionData every question is skipped and it returns 0/0/0. The D1 store
    // drops top-level questionData on the saved (?id=) load path, where scores
    // were already set from the saved result. Recomputing here with empty
    // questionData would overwrite those correct scores with zeros, which is
    // the "every short-quiz taker lands dead center (0/0/0)" bug. Only
    // (re)compute when we actually have the axis metadata; the ?data= and
    // session load paths populate it, so they still recompute as before.
    if (questionData.length === 0) {
      debugLog('❌ Skipping computeScores - questionData empty (would zero out loaded scores)');
      return;
    }

    async function computeScores() {
      debugLog('Computing scores with:', { answers: answers.length, questionIds: questionIds.length });
      const result = await calculateScores(answers, questionIds, quizType, undefined, isDebugMode, questionData);

      // DEBUG: Log the actual calculated scores vs what gets displayed
      debugLog('🎯 CALCULATED SCORES RESULT:');
      debugLog('📊 Main axes:', { economic: result.economic, governance: result.governance, social: result.social });
      debugLog('📊 Supplementary scores:', result.supplementary);

      setScores(result);
      setSupplementScores(result.supplementary);

      // DEBUG: Log what gets set in state
      debugLog('📊 Setting supplementScores state to:', result.supplementary);
    }
    computeScores();
  }, [dataLoaded, answers, questionIds, questionData, quizType, isDebugMode]);
  
  // Extract scores safely
  const economic = scores?.economic || 0;
  const governance = scores?.governance || 0;
  const social = scores?.social || 0;

  // Load grid data and find matching ideology
  useEffect(() => {
    // Wait for real scores. On mount `scores` is still null and economic/
    // governance default to 0; with the grid TSV already module-cached, the
    // old behavior resolved a dead-center ideology in a microtask before the
    // saved result arrived. The analytics latch below then captured that
    // center label, so virtually every result page showed the CENTER cell's
    // "% of quiz takers" (verified live: a Socialism result showing
    // Centrism's 11.1%), and quiz_results_viewed logged zeroed scores.
    if (!scores) return;
    async function loadIdeologyData() {
      try {
        const data = await loadGridData(quizType as 'short' | 'long');
        setGridData(data);

        let ideology;
        // Saved (?id=) result: show exactly the ideology stored at save time so
        // it never drifts when the matcher/scoring code changes between visits.
        // Only falls through to re-matching when the stored label can't resolve.
        const pinned = firebaseId
          ? findIdeologyByLabel(data, loadedMacroCellCode, loadedIdeologyLabel)
          : null;
        if (pinned) {
          debugLog('📌 Pinning saved result to stored ideology:', loadedIdeologyLabel, 'in cell', loadedMacroCellCode);
          ideology = pinned;
        } else if (quizType === 'long' && Object.keys(supplementScores).length > 0) {
          // The supplementary keys name the cell whose Phase 2 questions were
          // actually answered (EMGM-D -> EM-GM). That cell is the ground
          // truth for micro-ideology matching and for which six axes to show.
          // The previous behavior re-derived the cell from final scores when
          // it disagreed with the session, which orphaned the supplementary
          // data whenever a borderline taker drifted cells between Phase 2
          // selection and submit: the page then displayed the other cell's
          // axes with no matching scores, i.e. six zero bars.
          const phase2Cell = macroCellFromSupplementKeys(supplementScores) || sessionMacroCellCode;
          if (phase2Cell) {
            debugLog('🎯 Matching within the Phase 2 macro cell:', phase2Cell);
            ideology = await findDetailedIdeologyWithPredefinedMacroCell(
              data,
              phase2Cell,
              supplementScores
            );
          } else {
            debugLog('⚠️ No Phase 2 cell derivable; recalculating from scores');
            ideology = await findDetailedIdeologyWithSupplementary(
              data,
              economic,
              governance,
              supplementScores
            );
          }
        } else if (quizType === 'long') {
          // Long quiz but no supplementary scores yet - use Phase 1 only
          ideology = findDetailedIdeology(data, economic, governance, social);
        } else {
          // Short quiz
          ideology = findIdeologyByPosition(data, economic, governance);
        }
        
        setIdeologyData(ideology);

        // Track results viewed — once per page load only.
        if (!hasFiredResultsViewed.current) {
          hasFiredResultsViewed.current = true;
          capturePosthogEvent(posthog, 'quiz_results_viewed', {
            quiz_type: quizType,
            ideology: ideology?.ideology || ideology?.macroCellLabel || 'Unknown',
            economic_score: economic,
            governance_score: governance,
            social_score: social,
            is_shared: isShared
          }, { sendToServer: true });
        }

        // Load supplement axes for long quiz
        if (quizType === 'long' && ideology?.macroCellCode) {
          const axesMap = await loadSupplementAxes();
          const macroAxes = axesMap.get(ideology.macroCellCode) || [];
          setSupplementAxes(macroAxes);
        }
      } catch (error) {
        debugError('Error loading ideology data:', error);
      }
    }
    
    loadIdeologyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isShared/posthog intentionally omitted; loadIdeologyData depends only on score values
  }, [economic, governance, social, quizType, scores, supplementScores, sessionMacroCellCode, firebaseId, loadedIdeologyLabel, loadedMacroCellCode]);
  
  // Calculate display coordinates - for long quiz, adjust to cell position; for short quiz, use actual coordinates
  let displayX = economic;
  let displayY = governance;
  let displaySocial = social;
  
  if (quizType === 'long' && ideologyData?.coordinateRange) {
    // Parse the coordinate range to get cell boundaries
    const coordMatch = ideologyData.coordinateRange.match(/x:\s*(-?\d+\.?\d*)\s*to\s*(-?\d+\.?\d*),\s*y:\s*(-?\d+\.?\d*)\s*to\s*(-?\d+\.?\d*)/);
    if (coordMatch) {
      const [, xMinStr, xMaxStr, yMinStr, yMaxStr] = coordMatch;
      const xMin = parseFloat(xMinStr);
      const xMax = parseFloat(xMaxStr);
      const yMin = parseFloat(yMinStr);
      const yMax = parseFloat(yMaxStr);
      
      // Get cell center as the base position
      const centerX = (xMin + xMax) / 2;
      const centerY = (yMin + yMax) / 2;

      // Convert user's actual position to -10 to +10 scale for calculation
      const userX = economic / 10;
      const userY = governance / 10;

      // Calculate cell dimensions
      const cellWidth = xMax - xMin;
      const cellHeight = yMax - yMin;

      // Define safe zone with generous padding (20% from each edge)
      const padding = 0.2; // 20% padding from edges for safe zone
      const paddedXMin = xMin + (cellWidth * padding);
      const paddedXMax = xMax - (cellWidth * padding);
      const paddedYMin = yMin + (cellHeight * padding);
      const paddedYMax = yMax - (cellHeight * padding);

      // Calculate safe zone dimensions
      const safeWidth = paddedXMax - paddedXMin;
      const safeHeight = paddedYMax - paddedYMin;
      const safeCenterX = (paddedXMin + paddedXMax) / 2;
      const safeCenterY = (paddedYMin + paddedYMax) / 2;

      // Calculate user's offset from safe zone center with reduced influence (0.3 effect)
      const userOffsetX = (userX - centerX) * 0.3;
      const userOffsetY = (userY - centerY) * 0.3;

      // Add a small random offset within the safe zone
      const maxRandomOffset = 0.2; // 20% of safe zone dimension for randomization

      // Create a consistent random offset based on user's scores (deterministic per user)
      const seedX = Math.abs(economic * 137 + governance * 211) % 1000;
      const seedY = Math.abs(economic * 317 + governance * 419) % 1000;
      const randomOffsetX = (seedX / 1000 - 0.5) * safeWidth * maxRandomOffset;
      const randomOffsetY = (seedY / 1000 - 0.5) * safeHeight * maxRandomOffset;

      // Apply positioning within safe zone
      let adjustedX = safeCenterX + userOffsetX + randomOffsetX;
      let adjustedY = safeCenterY + userOffsetY + randomOffsetY;

      // Ensure we stay within the safe zone boundaries
      adjustedX = Math.max(paddedXMin, Math.min(paddedXMax, adjustedX));
      adjustedY = Math.max(paddedYMin, Math.min(paddedYMax, adjustedY));
      
      // Convert back to -100 to +100 scale
      displayX = adjustedX * 10;
      displayY = adjustedY * 10;
      
      // For social/social score, also apply muted positioning within reasonable bounds
      // This ensures 3D cube positioning is also consistent
      displaySocial = social * 0.75;
    }
  }
  
  // Convert to -10..10 scale for Vision alignment and display
  const x = toVisionScale(displayX);
  const y = toVisionScale(displayY);
  const z = toVisionScale(displaySocial);
  const alignment = findVisionAlignment(x, y, z);
  const resultLabel = quizType === 'short'
    ? (ideologyData?.macroCellLabel || alignment.label)
    : (ideologyData?.ideology || alignment.label);

  useEffect(() => {
    let cancelled = false;
    if (quizType !== 'long' || !questionData.length || reportQuestionCatalog !== null) return;
    loadAllQuestions('en').then(catalog => {
      if (!cancelled) setReportQuestionCatalog([...catalog.values()]);
    }).catch(() => {
      if (!cancelled) setReportQuestionCatalog([]);
    });
    return () => { cancelled = true; };
  }, [quizType, questionData.length, reportQuestionCatalog]);
  const reportInsight = useMemo(() => {
    if (quizType !== 'long' || !reportQuestionCatalog) return null;
    return resolveReportEvidence({
      questionData: questionData.map(q => ({ ...q, answer: q.answer ?? null })),
      result: {
        economicScore: economic, governanceScore: governance, socialScore: social,
        alignmentLabel: resultLabel, alignmentDescription: '',
      },
    }, reportQuestionCatalog).insight;
  }, [quizType, reportQuestionCatalog, questionData, economic, governance, social, resultLabel]);

  // Template-selection coordinates for the share artifacts (downloads and
  // email attachments). Each baked template carries a chrome caption derived
  // from its (econ, gov) BUCKET, while resultLabel comes from the matched
  // ideology (the supplementary matcher for the long quiz), so selecting a
  // template by raw position can return an artifact captioned with a
  // NEIGHBORING cell's ideology (user report: page/email said Social
  // Libertarianism, all three images said Progressivism). Selecting by the
  // matched ideology's own cell center instead round-trips exactly: for both
  // the 9x9/11-bucket and 3x3/11-bucket grids, every cell center lands in a
  // bucket whose baked label is that same cell. Falls back to the raw
  // position until the grid lookup resolves.
  let shareEcon = x;
  let shareGov = y;
  {
    const rangeMatch = ideologyData?.coordinateRange?.match(
      /x:\s*(-?\d+\.?\d*)\s*to\s*(-?\d+\.?\d*),\s*y:\s*(-?\d+\.?\d*)\s*to\s*(-?\d+\.?\d*)/
    );
    if (rangeMatch) {
      shareEcon = (parseFloat(rangeMatch[1]) + parseFloat(rangeMatch[2])) / 2;
      shareGov = (parseFloat(rangeMatch[3]) + parseFloat(rangeMatch[4])) / 2;
    }
  }

  // cube_interaction / results_view_mode_toggled / share_modal_opened|closed
  // events were retired in the 2026-06 telemetry trim. They generated ~18k
  // events/month and no insight referenced them. The 3D cube and share
  // modal still work; we just don't ship a PostHog event for every toggle.
  const handleCubeInteraction = (_type: 'rotate' | 'tooltip') => {};

  // Post-results activation: jump to the compare flow, carrying the just-finished
  // result via a handoff so the creator is linked without retaking. Shared by the
  // desktop (top-right) and mobile (right-after-cube) copies of the CTA.
  const handleCompareClick = async () => {
    capturePosthogEvent(posthog, 'group_cta_from_results_clicked', {
      quiz_type: quizType,
      result_label: resultLabel,
    }, { sendToServer: true });
    const attribution = groupIdParam
      ? { parentGroupId: groupIdParam, creationSource: 'group_results' }
      : { creationSource: 'quiz_results' };
    const fallbackParams = new URLSearchParams({ source: attribution.creationSource });
    if (attribution.parentGroupId) fallbackParams.set('parent', attribution.parentGroupId);
    if (resultLabel) fallbackParams.set('seed', resultLabel);
    const seedUrl = `/group/new?${fallbackParams}`;
    if (docId) {
      const token = await mintHandoff('quiz_to_compare', {
        quizResponseId: docId,
        quizType,
        ideologyName: resultLabel,
        attribution,
      });
      router.push(token ? `/group/new?h=${token}` : seedUrl);
    } else {
      router.push(seedUrl);
    }
  };

  // Save the quiz result (skip if this is a shared result or debug mode)
  useEffect(() => {
    // Don't save if we don't have actual data yet. The `!scores` guard is
    // load-bearing: computeScores is async, so on the ?data= fallback path
    // this effect used to fire in the same flush that set answers/dataLoaded
    // and persist a 0/0/0 "Pragmatic Moderate" row, then latch hasSaved so
    // the real scores never saved (8k+ such rows found in D1, and the address
    // bar was rewritten to the dead ?id=, poisoning the permanent link).
    // `!ideologyData` is load-bearing for the same reason. Until the grid
    // resolves, the only label available is `alignment.label`, the coarse
    // Vision octant persona: "Cooperative Dreamer", "Underground Organizer",
    // "Pragmatic Moderate". Those are not among the canonical 81 long or 9
    // short labels, so a row saved with one is invisible to the "% of quiz
    // takers" stats, resolves to no dossier for the report and its preview,
    // and would hand a paying buyer the "this dossier is being updated" page.
    // 43 such rows were written between the June migration and 2026-08-20 at
    // roughly 0.4% of saves, purely on whether the grid fetch beat the score
    // computation. If the grid never resolves the page has no ideology to
    // show either, so waiting costs nothing a broken load had not already
    // cost, and it never persists a label the rest of the system rejects.
    if (answers.length === 0 || !dataLoaded || !scores || !ideologyData) {
      return;
    }
    
    if (firebaseId || hasSaved.current || isShared || isDebugMode) {
      if (isDebugMode && !hasSaved.current) {
        debugLog('🐛 DEBUG MODE: Skipping Firebase save');
        hasSaved.current = true;
      }
      return;
    }
    hasSaved.current = true;
    
    const macroCellCode = ideologyData?.macroCellCode || macroCellFromScores(economic, governance);

    // Get skip statistics if available
    const skipStats = (window as Window & { __skipStats?: { totalSkipped: number; skipsByAxis: { economic: number; governance: number; social: number } } }).__skipStats;

    // Stash a payload for the long-form report renderer at /quiz/results/report.
    // Includes question text by id so the report can quote answers without a TSV refetch.
    try {
      const session = getCurrentSession();
      const questionTexts: Record<number, string> = {};
      if (session?.questions) {
        for (const q of session.questions) {
          questionTexts[q.id] = q.text;
        }
      }
      const reportResult = {
        answers,
        quizType: quizType as 'short' | 'long',
        questionData: questionData.length > 0
          ? questionData.map(q => ({
              id: q.id,
              axis: q.axis,
              agreeDir: q.agreeDir,
              answer: q.answer ?? null,
              ...(q.supplementAxis ? { supplementAxis: q.supplementAxis } : {})
            }))
          : undefined,
        result: {
          economicScore: economic,
          governanceScore: governance,
          socialScore: social,
          alignmentLabel: resultLabel,
          alignmentDescription: ideologyData.explanation,
          macroCellCode,
        },
      };
      sessionStorage.setItem(
        'votely_latest_report_payload',
        JSON.stringify({ result: reportResult, questionTexts }),
      );
    } catch (e) {
      debugError('Failed to stash report payload:', e);
    }

    // Persistence requires `answer: number | null`; ScoringQuestionData treats
    // it as optional. Materialize undefined to null at the boundary, and keep
    // originalId: compare creator-linkage and the paid report both need it.
    const persistedQuestionData = questionData.length > 0
      ? questionData.map(q => ({
          id: q.id,
          axis: q.axis,
          agreeDir: q.agreeDir,
          answer: q.answer ?? null,
          ...(q.originalId ? { originalId: q.originalId } : {}),
          ...(q.supplementAxis ? { supplementAxis: q.supplementAxis } : {})
        }))
      : undefined;

    // originalId -> answer map, mirrored from the primary save path in
    // quiz-page-client: result_json is the only payload that round-trips on
    // D1, and lib/quiz/locked-answers.ts reads answersByOriginalId from it.
    const answersByOriginalId: Record<string, number> = {};
    questionData.forEach(q => {
      if (q.originalId && q.answer !== undefined && q.answer !== null) {
        answersByOriginalId[q.originalId] = q.answer;
      }
    });

    saveQuizResult({
      answers,
      quizType: quizType as 'short' | 'long',
      questionData: persistedQuestionData,
      skipStats: skipStats || undefined,
      result: {
        economicScore: economic,
        governanceScore: governance,
        socialScore: social,
        alignmentLabel: resultLabel,
        alignmentDescription: ideologyData.explanation,
        macroCellCode: macroCellCode,
        ...(scores?.supplementary && Object.keys(scores.supplementary).length > 0 && {
          supplementaryScores: scores.supplementary
        }),
        gridPosition: {
          economic: economic,
          social: social
        },
        ...(Object.keys(answersByOriginalId).length > 0 && { answersByOriginalId }),
        // D1 drops top-level questionData (no column); nest a copy inside
        // result_json so paid reports keep their answer-derived sections.
        ...(persistedQuestionData && { questionData: persistedQuestionData })
      },
      phase: quizType === 'long' && scores?.supplementary && Object.keys(scores.supplementary).length > 0 ? 2 : 1
    })
      .then(id => {
        debugLog('Quiz result saved successfully with ID:', id);
        capturePosthogEvent(posthog, 'quiz_result_saved', {
          quiz_type: quizType,
          document_id: id,
          answers_count: answers.length,
          skipped_count: skipStats?.totalSkipped ?? 0,
          phase: quizType === 'long' && scores?.supplementary && Object.keys(scores.supplementary).length > 0 ? 'phase2' : 'phase1'
        }, { sendToServer: true });
        setDocId(id);
        
        // Rewrite the address bar to the permanent form of this result so a
        // copied link stays valid. Built from a whitelist (id, type, groupId)
        // instead of mutating window.location, so one-off session params
        // (data payloads, shared/debug flags, utm noise) never leak into
        // links people copy.
        if (id && !firebaseId) {
          const params = new URLSearchParams({ id, type: quizType });
          if (groupIdParam) params.set('groupId', groupIdParam);
          window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
        }
      })
      .catch(error => {
        debugError('Failed to save quiz result:', error);
        debugError('Error details:', error.message, error.code);
        capturePosthogEvent(posthog, 'quiz_result_save_failed', {
          quiz_type: quizType,
          error_message: error?.message,
          error_code: error?.code
        }, { sendToServer: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot save; only score-determining inputs are tracked to avoid duplicate writes. `scores` is included so the effect re-fires when the async computation lands (economic alone misses the case where the real value is also 0).
  }, [answers, scores, economic, social, alignment, isShared, dataLoaded, firebaseId]);

  // State for dynamic data
  const [resultPercentage, setResultPercentage] = useState<number | null>(null);
  const [totalQuizCount, setTotalQuizCount] = useState<number | null>(null);
  const [_politicalGroups, setPoliticalGroups] = useState<Array<{name: string, description: string, match: number}>>([]);
  const [_surprisingAlignments, setSurprisingAlignments] = useState<Array<{group: string, commonGround: string}>>([]);
  const [waitlistCount, setWaitlistCount] = useState<number>(0);

  // Load analytics data
  useEffect(() => {
    // Skip if no ideology data yet or already loaded
    if (!ideologyData || hasLoadedAnalytics.current) {
      return;
    }

    hasLoadedAnalytics.current = true;

    const loadAnalyticsData = async () => {
      debugLog('Starting analytics data load...');

      // First test the basic Firebase connection
      const isConnected = await testFirebaseConnection();
      if (!isConnected) {
        debugError('Firebase connection test failed, aborting analytics load');
        return;
      }

      // Load all analytics data in parallel. getResultPercentage reads
      // cachedPercentages/label_{quizType}_{slug(resultLabel)}; resultLabel is
      // the same alignmentLabel value the writer stored, computed at line
      // 937-939 from ideologyData?.ideology / macroCellLabel with fallback.
      const [percentage, totalCount, groupMatches, waitlist] = await Promise.all([
        getResultPercentage(resultLabel, quizType as 'short' | 'long'),
        getTotalQuizCount(),
        getPoliticalGroupMatches(economic, governance, social),
        getWaitlistCount()
      ]);

      // Get surprising alignments, excluding the groups already shown in "You Align With"
      const excludeGroups = groupMatches.map(group => group.name);
      const surprisingMatches = await getSurprisingAlignments(economic, governance, social, excludeGroups);

      debugLog('Analytics data loaded:', { percentage, totalCount, groupMatches, surprisingMatches });

      setResultPercentage(percentage);
      setTotalQuizCount(totalCount);
      setPoliticalGroups(groupMatches);
      setSurprisingAlignments(surprisingMatches);
      setWaitlistCount(waitlist);
    };

    loadAnalyticsData().catch(error => {
      debugError('Error loading analytics data:', error);
    });
  }, [economic, governance, social, quizType, ideologyData]);

  // Handle error states within the component return
  if (!answersParam && !sessionIdParam && !dataParam && !firebaseId) {
    return <div className="min-h-screen bg-linear-to-b from-background to-primary/10 p-4 md:p-8 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-foreground mb-4">No results found</h1>
        <p className="text-foreground/60">Please take the quiz first.</p>
      </div>
    </div>;
  }
  
  // Persisted-lookup miss. Show a graceful "not found" instead of
  // hanging on the loading spinner so the page doesn't read as broken
  // when an old share link points at a row that genuinely doesn't
  // exist anymore.
  if (resultMissing) {
    return <div className="min-h-screen bg-linear-to-b from-background to-primary/10 p-4 md:p-8 flex items-center justify-center">
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-bold text-foreground mb-4">We couldn&apos;t find that result</h1>
        <p className="text-foreground/70 mb-6">The link may be from an older version of the site, or the saved result has expired. You can take the quiz again to generate a fresh shareable result.</p>
        <a href="/quiz?type=long" className={buttonClasses()}>Take the quiz</a>
      </div>
    </div>;
  }

  // Check if data is still loading (including Firebase loading)
  if (!dataLoaded || isLoadingFirebase) {
    return <div className="min-h-screen bg-linear-to-b from-background to-primary/10 p-4 md:p-8 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-foreground mb-4">Loading quiz data...</h1>
        <div className="animate-pulse">
          <div className="w-16 h-16 mx-auto bg-[#2a4173] rounded-full"></div>
        </div>
      </div>
    </div>;
  }
  
  // Check if we have valid data after loading
  if (dataLoaded && answers.length === 0) {
    return <div className="min-h-screen bg-linear-to-b from-background to-primary/10 p-4 md:p-8 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-foreground mb-4">Invalid quiz data</h1>
        <p className="text-foreground/60">Unable to load quiz results. Please try taking the quiz again.</p>
        {sessionIdParam && (
          <p className="text-sm text-text-muted mt-2">Session ID: {sessionIdParam}</p>
        )}
        <a href="/quiz" className={buttonClasses({ className: 'mt-4' })}>Start New Quiz</a>
      </div>
    </div>;
  }
  
  if (!scores) {
    return <div className="min-h-screen bg-linear-to-b from-background to-primary/10 p-4 md:p-8 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-foreground mb-4">Calculating results...</h1>
        <div className="animate-pulse">
          <div className="w-16 h-16 mx-auto bg-[#2a4173] rounded-full"></div>
        </div>
        {answersParam && !sessionIdParam && answers.length === 50 && (
          <div className="mt-8 text-sm text-text-muted max-w-md mx-auto px-4">
            <p className="mb-2">Using approximate scoring from legacy URL.</p>
            <p>For accurate results with proper question tracking, please complete the quiz from the beginning.</p>
            <a href="/quiz?type=long" className={buttonClasses({ variant: 'link', className: 'mt-4' })}>Start New Quiz</a>
          </div>
        )}
      </div>
    </div>;
  }
  
  // Show loading state while fetching from Firebase
  if (isLoadingFirebase) {
    return (
      <div className="min-h-screen bg-linear-to-b from-background to-primary/10 p-4 md:p-8 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700 mx-auto"></div>
          <p className="text-xl text-foreground">Loading your results...</p>
        </div>
      </div>
    );
  }

  return (
    // Keyed so React replaces the loading/error DOM instead of morphing it
    // into this tree: the reused node used to jump from the centered spinner
    // to the full-bleed star layer, a 0.44 layout shift on every quiz finish.
    <div key="results-view" className="min-h-screen bg-linear-to-b from-background to-primary/10 p-4 md:p-8 relative overflow-hidden">
      {/* Star background */}
      <div className="absolute inset-0 pointer-events-none select-none z-0">
        {starPattern.map((star, i) => (
          <svg
            key={i}
            className={`absolute opacity-20 ${star.size} ${star.color} ${star.rotate}`}
            style={{
              top: `${Math.round((star.top / 100) * STAR_FIELD_HEIGHT)}px`,
              left: `${star.left}%`,
            }}
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <polygon points="12,2 15,10 23,10 17,15 19,23 12,18 5,23 7,15 1,10 9,10" />
          </svg>
        ))}
      </div>
      
      {/* Main content */}
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Page navbar: Votely brand across the top of the whole results page.
            Doubles as the screenshot watermark, full-page phone captures carry
            the brand + URL the Reddit screenshot monitor keys on. */}
        <nav className="flex items-center justify-between mb-6">
          <Link href="/" className="flex items-center gap-2">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/votely-cube-v4.svg" alt="Votely" width={30} height={30} className="block" />
            <span className="font-bold text-xl tracking-tight text-slate-800">Votely</span>
          </Link>
          <span className="text-sm text-slate-400">votelyquiz.com</span>
        </nav>
        {/* Debug Mode Banner */}
        {isDebugMode && (
          <div className="mb-6 p-4 bg-yellow-100 border-2 border-yellow-400 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🐛</span>
              <span className="font-bold text-yellow-800">DEBUG MODE - Results not saved to database</span>
            </div>
            <p className="text-sm text-yellow-700 mt-1">
              Check the browser console for detailed scoring calculations
            </p>
          </div>
        )}

        {/* Post-payment status banner. /api/checkout/return redirects here
            with ?purchased=<reason> on its failure paths (session retrieve
            hiccup, async payment pending, signing failure, ...). These used
            to be silently ignored: a buyer who just paid landed on a page
            that looked exactly like before paying. */}
        {purchasedParam && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-300 rounded-2xl text-sm text-amber-900">
            <div className="text-xs uppercase tracking-wider font-semibold mb-1">
              Payment received
            </div>
            {purchasedParam === 'pending' ? (
              <p>
                Your payment is still clearing. As soon as it does, your Personal
                Political Profile link and PDF will arrive by email; nothing else
                is needed from you.
              </p>
            ) : (
              <p>
                Your payment went through, but the automatic redirect to your
                report hit a snag. The report email (with your PDF attached) is on
                its way; if it has not arrived within an hour, reply to your
                Stripe receipt and I will sort it out personally.
              </p>
            )}
          </div>
        )}

        {/* Group context banner, shown when this result came from a group quiz.
            Surfaces the path back so the user doesn't lose the group context
            while reading their personal result. */}
        {groupIdParam && (
          <div className="mb-6 p-4 bg-linear-to-r from-slate-100 to-slate-50 border border-slate-200 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-800 mb-1">
                Part of a group quiz
              </div>
              <p className="text-sm text-foreground/80">
                You answered as a member of a group. Your detailed result is below;
                the group sees your position on the cube and your answers to the
                12 shared questions.
              </p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <a
                href={`/group/${groupIdParam}`}
                className={buttonClasses({ variant: 'secondary', size: 'sm' })}
              >
                Back to lobby
              </a>
              <a
                href={`/group/${groupIdParam}/reveal`}
                className={buttonClasses({ size: 'sm' })}
              >
                See group reveal
              </a>
            </div>
          </div>
        )}

        {groupIdParam && <div className="mb-6"><GroupRevealReminder groupId={groupIdParam} /></div>}

        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          {isShared ? (
            <div className="inline-block bg-slate-100 border border-slate-300 text-slate-900 px-4 py-3 rounded-full text-sm font-medium mb-4">
              Viewing shared results, take the quiz yourself at{' '}
              <a
                href="https://votelyquiz.com"
                className="font-semibold underline hover:no-underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                votelyquiz.com
              </a>
            </div>
          ) : null}
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">Your Political Alignment</h1>
          <p className="text-foreground/60 mt-2">
            <span>
              You&rsquo;re {totalQuizCount === null
                ? 'one of thousands of'
                : `1 of ${totalQuizCount.toLocaleString()}`} quiz takers
            </span>
          </p>
        </div>

        {/* 2x1 Grid Layout with equal height columns */}
        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-6 lg:items-start">
          {/* Left Column - Political Compass + Founding Supporter + AboutCreator */}
          <div className="flex flex-col gap-6 contents lg:flex lg:flex-col lg:h-full">
            {/* Political Compass */}
            <div ref={graphRef} className="bg-white border border-[#16223d]/10 ring-1 ring-[#16223d]/5 rounded-2xl shadow-[0_4px_6px_rgba(28,35,33,0.06),0_24px_48px_rgba(28,35,33,0.08)] p-5 sm:p-8 relative order-1 lg:order-0">
              {/* View toggle + instructions, directly above the cube. */}
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-slate-600">
                  {view3D ? 'Click and drag to rotate' : 'Click and drag to pan'}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-600">2D</span>
                  <button
                    onClick={() => setView3D(!view3D)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-hidden focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 ${
                      view3D ? 'bg-[#2a4173]' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        view3D ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="text-sm text-slate-600">3D</span>
                </div>
              </div>

              {view3D ? (
                /* Reserve ResultCube's real footprint (w-full aspect-square,
                   see components/ResultCube.tsx) while its lazy chunk loads.
                   A fixed h-[400px] fallback mismatched the square cube and
                   shifted the page when the chunk mounted on every results
                   view (view3D defaults on): a CLS source for all quiz
                   takers. aspect-square keeps the swap shift-free. */
                <Suspense fallback={<div className="w-full aspect-square flex items-center justify-center text-slate-600">Loading 3D visualization...</div>}>
                  {hasWebGL() ? (
                    <ResultCube
                      // Use the retrofitted display position (the same source the
                      // 2D grid uses) so the cube and the 9x9 grid agree on where
                      // the dot sits. For the long quiz this reflects the Phase-2
                      // micro-cell, not the raw Phase-1 econ/gov. Short quiz:
                      // displayX/displayY === economic/governance (no retrofit).
                      // z stays raw social; the grid has no social axis.
                      x={displayX}
                      y={displayY}
                      z={social}
                      ideologyLabel={alignment.label}
                      onInteraction={handleCubeInteraction}
                      hideDropLines
                    />
                  ) : (
                    <ResultCubeFallback
                      x={displayX}
                      y={displayY}
                      z={social}
                      ideologyLabel={alignment.label}
                    />
                  )}
                </Suspense>
              ) : (
                <AdaptivePoliticalCompass point={{ x, y }} quizType={quizType === 'short' ? 'short' : 'long'} interactive />
              )}

              {/* econ/gov must be the -10..+10 vision scale, and specifically
                  the matched ideology's CELL CENTER (shareEcon/shareGov, see
                  their definition) so the template's baked caption always
                  equals resultLabel. social only moves the dot, never the
                  caption. quizType picks the short (3x3 macro) vs long
                  (9x9 detailed) baked variant. */}
              <CompactShareRow
                econ={shareEcon}
                gov={shareGov}
                social={z}
                ideologyLabel={resultLabel}
                resultName={resultLabel}
                quizType={quizType === 'short' ? 'short' : 'long'}
                quizResponseId={docId ?? firebaseId ?? undefined}
                footer={
                  <ResultSatisfaction
                    quizResponseId={docId ?? firebaseId ?? undefined}
                    quizType={quizType}
                    resultLabel={resultLabel}
                  />
                }
                onSaved={({ format, method }) => {
                  // Referral signal: the user saved or shared a result artifact.
                  // The old InlineShareSection emitted this; CompactShareRow
                  // replaced it but the callback was never wired, so sharing has
                  // been untracked since the 2026-06 share refactor.
                  capturePosthogEvent(posthog, 'share_artifact_saved', {
                    format,
                    method,
                    quiz_type: quizType,
                    result_label: resultLabel,
                  }, { sendToServer: true });
                }}
              />
            </div>

            {/* Compare / group-quiz CTA: directly below the cube in the left
                column, both mobile and desktop. */}
            <CompareCta onInvite={handleCompareClick} detailed={quizType === 'short'} className="order-2 lg:order-0" />

            {/* Short quiz has no paid-report upsell (PdfReportUpsell above is
                long-only), which left the left column sparse. Promote the long
                quiz here, framed around the Personal Political Profile report,
                to fill it and drive long-quiz starts. */}
            {quizType === 'short' && (
              <LongQuizReportCard
                showCarryOver={hasCompletedShortHere}
                onStart={() => {
                  capturePosthogEvent(posthog, 'long_quiz_cta_clicked', { result_label: resultLabel, location: 'report_card' }, { sendToServer: true });
                  router.push('/quiz?type=long');
                }}
                className="order-4 lg:order-0"
              />
            )}

            {/* About the creator: in the left column on desktop (below the report
                CTA), last on mobile (order preserved). */}
            <div className="order-5 lg:order-0">
              <AboutCreator />
            </div>

          </div>

          {/* Right Column - User Results + Alignment sections */}
          <div className="flex flex-col gap-6 contents lg:flex lg:flex-col lg:h-full">
            {/* User Results - Single card containing everything */}
            {/* Keyed on dossier readiness: when ideologyData lands, several
                blocks above the score bars inflate (explanation, examples),
                which read as a ~0.1 layout shift. Remounting the card instead
                of patching it keeps the reflow out of CLS. */}
            <div key={ideologyData ? 'ideology-full' : 'ideology-base'} className="bg-white border border-[#16223d]/10 ring-1 ring-[#16223d]/5 rounded-2xl shadow-[0_4px_6px_rgba(28,35,33,0.06),0_24px_48px_rgba(28,35,33,0.08)] p-8 pb-12 flex flex-col h-full order-3 lg:order-0">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-700 mb-2 wrap-break-word hyphens-auto">
                {quizType === 'short' ? (ideologyData?.macroCellLabel || alignment.label) : (ideologyData?.ideology || alignment.label)}
              </h2>
              {/* Confidence framing for the 12-question quick read. Without
                  it the broad-region label reads as a definitive verdict,
                  and near-boundary takers who retake and land in a different
                  region conclude the quiz is broken (Aug 2026: a retaker got
                  opposite corners across two sittings and reported the site
                  as "not working"). The full upsell block sits at the bottom
                  of this card; this line sets expectations at first glance. */}
              {quizType === 'short' && (
                <p className="text-sm text-foreground/60 mb-2">
                  The 12-question quick version places you in one of nine broad regions. The full quiz below pins down your exact ideology.
                </p>
              )}
              <p className="text-sm text-foreground/60 mb-4">
                {resultPercentage === null
                  ? 'Loading percentage...'
                  : resultPercentage === 0
                    ? 'Less than 1% of quiz takers got this result.'
                    : `${resultPercentage.toFixed(1)}% of quiz takers get this result`}
              </p>
              <p className="text-foreground/80 mb-6">{ideologyData?.explanation || alignment.description}</p>

              {quizType === 'long' && ideologyData && (
                <PdfReportUpsell
                  key={docId ?? firebaseId ?? 'unsaved-report'}
                  ideologyName={ideologyData.ideology || resultLabel}
                  quizType={quizType}
                  resultLabel={resultLabel}
                  quizResponseId={docId ?? firebaseId ?? undefined}
                  insight={reportInsight}
                  insightPending={questionData.length > 0 && reportQuestionCatalog === null}
                />
              )}
              
              {/* Recent Examples Section */}
              {ideologyData?.examples && (
                <div className="bg-gray-50 rounded-lg p-4 mb-8">
                  <h3 className="text-sm font-semibold text-slate-600 mb-2">Recent Examples</h3>
                  <p className="text-sm text-slate-700">{ideologyData.examples}</p>
                </div>
              )}
              
              {/* Score Bars.
                  These report the user's ACTUAL axis scores (economic /
                  governance / social), never displayX/displayY/displaySocial.
                  Those are a layout transform: for the long quiz they snap to
                  the matched ideology's grid cell (cell centre + 30% of the
                  user's offset + a deterministic jitter, clamped into the
                  cell's inner 60%) so the cube marker lands inside the region
                  the result is named after. They are not measurements. Nine of
                  the 81 ideologies sit in a cell spanning x: -1.11 to 1.11
                  (Centrism, Social Libertarianism, Welfare Capitalism,
                  Third-Way Labour, Minarchism, Agorism, State Liberalism,
                  Integralism, Autocratic Theocracy), which pinned the economic
                  bar to at most 6.7% for ~25% of long-quiz takers whatever
                  they answered, and the cell centre could sit on the opposite
                  side of zero from the real score, printing "Right" for a
                  left-leaning taker. Reported Aug 2026 by a group who all saw
                  0% on the economic axis. */}
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between mb-2 flex-wrap gap-1">
                    <span className="text-xs md:text-sm font-medium text-slate-700">Economic Score</span>
                    <span className="text-xs md:text-sm text-foreground/60">{economic < 0 ? 'Left' : 'Right'} ({Math.abs(economic).toFixed(1)}%)</span>
                  </div>
                  <div className="relative">
                    <div className="w-full bg-gray-200 rounded-full h-2.5 relative">
                      <div
                        className="bg-[#2a4173] h-2.5 rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.abs(economic) / 2}%`,
                          marginLeft: economic < 0 ? `${50 - Math.abs(economic) / 2}%` : '50%'
                        }}
                      />
                    </div>
                    {/* Center line */}
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-0.5 h-2.5 bg-gray-400"></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between mb-2 flex-wrap gap-1">
                    <span className="text-xs md:text-sm font-medium text-slate-700">Governance Score</span>
                    <span className="text-xs md:text-sm text-foreground/60">{governance > 0 ? 'Authoritarian' : 'Libertarian'} ({Math.abs(governance).toFixed(1)}%)</span>
                  </div>
                  <div className="relative">
                    <div className="w-full bg-gray-200 rounded-full h-2.5 relative">
                      <div
                        className="bg-[#2a4173] h-2.5 rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.abs(governance) / 2}%`,
                          marginLeft: governance < 0 ? `${50 - Math.abs(governance) / 2}%` : '50%'
                        }}
                      />
                    </div>
                    {/* Center line */}
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-0.5 h-2.5 bg-gray-400"></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between mb-2 flex-wrap gap-1">
                    <span className="text-xs md:text-sm font-medium text-slate-700">Social Score</span>
                    <span className="text-xs md:text-sm text-foreground/60">{social < 0 ? 'Progressive' : 'Conservative'} ({Math.abs(social).toFixed(1)}%)</span>
                  </div>
                  <div className="relative">
                    <div className="w-full bg-gray-200 rounded-full h-2.5 relative">
                      <div
                        className="bg-[#2a4173] h-2.5 rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.abs(social) / 2}%`,
                          marginLeft: social < 0 ? `${50 - Math.abs(social) / 2}%` : '50%'
                        }}
                      />
                    </div>
                    {/* Center line */}
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-0.5 h-2.5 bg-gray-400"></div>
                  </div>
                </div>
              </div>
              
              {/* Supplement Axes for Long Quiz */}
              {quizType === 'long' && supplementAxes.length > 0 && (
                <SupplementAxes
                  axes={supplementAxes}
                  scores={supplementScores}
                  macroCell={ideologyData?.macroCellLabel || ''}
                  macroCellColor={ideologyData?.macroCellCode ? getMacroCellColor(ideologyData.macroCellCode) : undefined}
                />
              )}
              
              {/* You Align With section - integrated for both quiz types */}
              {ideologyData && (
                <div className="mt-8 pt-8 border-t">
                  <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
                    You align with
                  </h3>
                  
                  <div className="space-y-4 mb-8">
                    {/* Single alignment */}
                    {(() => {
                      let alignmentData = null;
                      let alignmentLabel = ideologyData.alignIdeology1;
                      let alignmentColor = '#22c55e';
                      
                      if (quizType === 'short') {
                        alignmentData = findGridDataByIdeology(gridData, ideologyData.alignIdeology1);
                        if (alignmentData) {
                          alignmentLabel = alignmentData.macroCellLabel;
                          alignmentColor = getMacroCellColor(alignmentData.macroCellCode);
                        }
                      } else {
                        alignmentData = gridData.find(cell => cell.ideology === ideologyData.alignIdeology1);
                        if (alignmentData) {
                          alignmentColor = getMacroCellColor(alignmentData.macroCellCode);
                        }
                      }
                      
                      return (
                        <div 
                          className="border-l-4 pl-6 pr-4 bg-gray-50/50 rounded-r-lg py-4" 
                          style={{ 
                            borderLeftColor: alignmentColor,
                            boxShadow: `inset 4px 0 0 ${alignmentColor}, 0 1px 3px rgba(0,0,0,0.05)`
                          }}
                        >
                          <h4 className="font-semibold text-foreground wrap-break-word hyphens-auto">{alignmentLabel}</h4>
                          <p className="text-sm text-foreground/60">{ideologyData.alignIdeology1Text}</p>
                        </div>
                      );
                    })()}
                  </div>
                  
                  {/* What Might Surprise You */}
                  <div>
                    <h4 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      What might surprise you
                    </h4>
                    <div className="space-y-3">
                      {(() => {
                        let surpriseData = null;
                        let surpriseLabel = ideologyData.surpriseIdeology1;
                        let surpriseColor = '#f59e0b';
                        
                        if (quizType === 'short') {
                          surpriseData = findGridDataByIdeology(gridData, ideologyData.surpriseIdeology1);
                          if (surpriseData) {
                            surpriseLabel = surpriseData.macroCellLabel;
                            surpriseColor = getMacroCellColor(surpriseData.macroCellCode);
                          }
                        } else {
                          surpriseData = gridData.find(cell => cell.ideology === ideologyData.surpriseIdeology1);
                          if (surpriseData) {
                            surpriseColor = getMacroCellColor(surpriseData.macroCellCode);
                          }
                        }
                        
                        return (
                          <div 
                            className="border-l-4 pl-6 pr-4 bg-gray-50/50 rounded-r-lg py-4" 
                            style={{ 
                              borderLeftColor: surpriseColor,
                              boxShadow: `inset 4px 0 0 ${surpriseColor}, 0 1px 3px rgba(0,0,0,0.05)`
                            }}
                          >
                            <h5 className="font-medium text-foreground wrap-break-word hyphens-auto">{surpriseLabel}</h5>
                            <p className="text-sm text-foreground/70">{ideologyData.surpriseIdeology1Text}</p>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* Long Quiz CTA for Short Quiz Users */}
              {quizType === 'short' && (
                <div className="mt-8 pt-8 border-t">
                  <h3 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                    You have your region. Now get your exact ideology.
                  </h3>
                  <p className="text-foreground/80 mb-4">
                    The long quiz adds six sub-axes built for your region, which is how it tells the nine
                    ideologies inside it apart.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-4 mb-6">
                    <p className="text-sm text-slate-900">
                      <strong>The full quiz goes further.</strong>
                    </p>
                    <ul className="mt-2 space-y-1 text-sm text-slate-900">
                      <li>• It names your exact ideology, one of 81.</li>
                      <li>• It is the version the paid report is built from.</li>
                      <li>• It shows the people you agree with and never expected to.</li>
                    </ul>
                    {hasCompletedShortHere && (
                      <p className="mt-3 text-sm text-slate-900">
                        <strong>Your answers carry over</strong>, so you start about a fifth of the way in.
                      </p>
                    )}
                  </div>
                  <Button
                    onClick={() => {
                      capturePosthogEvent(posthog, 'long_quiz_cta_clicked', {
                        result_label: resultLabel
                      }, { sendToServer: true });
                      router.push('/quiz?type=long');
                    }}
                    fullWidth
                  >
                    Take the full 60-question quiz
                    <span className="text-sm font-normal opacity-90">(free, about 10 minutes)</span>
                  </Button>
                </div>
              )}

            </div>


          </div>

        </div>

      </div>

    </div>
  );
}
